import { useMemo, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { NAV_ITEMS, NAV_GROUPS, NAV_OVERVIEW, type NavGroup } from "./routes.tsx";
import { TenantSwitcher } from "../chrome/TenantSwitcher.tsx";
import { ConnectionIndicator } from "../chrome/ConnectionIndicator.tsx";
import { useAuth } from "../auth/index.ts";
import { controlplaneLogo } from "../brand.ts";
import { useTheme } from "../state/theme.tsx";
import { UserMenu } from "./UserMenu.tsx";

/**
 * The authenticated chrome, modelled on the AgentDNA dashboard shell: a persistent LEFT SIDEBAR
 * (brand, the primary telemetry navigation, and the personal Account/Team links pinned to the
 * bottom) and a TOP BAR (a breadcrumb on the left; the tenant switcher, theme toggle,
 * connection indicator and command-palette hint on the right). The time range now renders
 * per-screen next to each page's live/pause control.
 *
 * Navigation uses `NavLink` so the active screen is marked with `aria-current="page"` — status by
 * state, legible to assistive technology (design 12.4). The server still enforces every route and
 * `/api/*` call regardless of what the chrome shows.
 */

// -------------------------------------------------------------------------------------------------
// Inline icon set (no icon dependency). Each is a 20×20 stroke glyph that inherits `currentColor`.
// -------------------------------------------------------------------------------------------------

type IconProps = { readonly name: string };

const PATHS: Record<string, ReactNode> = {
  intents: <><path d="M12 3v6" /><circle cx="12" cy="13" r="3" /><path d="M12 16v5" /><path d="M5 21h14" /></>,
  agents: <><circle cx="12" cy="7" r="3" /><rect x="4" y="13" width="16" height="8" rx="2" /><path d="M9 17h6" /></>,
  "discovered-agents": <><circle cx="10" cy="9" r="3" /><path d="M4 20c0-3.3 2.7-5 6-5" /><circle cx="17" cy="16" r="3" /><path d="M19.2 18.2L21 20" /></>,
  requests: <><path d="M4 4h16v12H5l-1 4z" /><path d="M8 9h8M8 12h5" /></>,
  interactions: <><circle cx="6" cy="7" r="2.5" /><circle cx="18" cy="17" r="2.5" /><path d="M8 8l8 7" /><path d="M16 7h3v3M8 17H5v-3" /></>,
  observability: <><circle cx="12" cy="12" r="3" /><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /></>,
  overview: <><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></>,
  decisions: <><path d="M4 6h16" /><path d="M4 12h10" /><path d="M4 18h7" /><circle cx="18" cy="15" r="3" /></>,
  identity: <><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 4-6 8-6s8 2 8 6" /></>,
  "mcp-gateway": <><rect x="3" y="4" width="18" height="7" rx="1" /><path d="M7 7.5h.01M11 7.5h.01" /><path d="M12 11v4" /><path d="M7 15h10" /><path d="M7 15v3M12 15v3M17 15v3" /></>,
  "identity-mcp": <><circle cx="12" cy="7" r="3" /><path d="M6 20c0-3.3 2.7-5 6-5s6 1.7 6 5" /><rect x="14" y="3" width="7" height="5" rx="1" /><path d="M16.5 5.5h2" /></>,
  assurance: <><path d="M12 3l7 3v5c0 4.5-3 7-7 8-4-1-7-3.5-7-8V6z" /><path d="M9 11l2 2 4-4" /></>,
  enforcement: <><path d="M12 3l8 3v6c0 5-3.5 7.5-8 9-4.5-1.5-8-4-8-9V6z" /></>,
  integrity: <><path d="M20 7L9 18l-5-5" /></>,
  posture: <><path d="M3 12a9 9 0 1 0 18 0" /><path d="M12 12V3" /><path d="M12 12l6 4" /></>,
  compliance: <><rect x="5" y="4" width="14" height="17" rx="1" /><path d="M9 4V3h6v1" /><path d="M8 11l2.5 2.5L15 9" /><path d="M8 17h8" /></>,
  traces: <><circle cx="5" cy="6" r="2" /><circle cx="19" cy="6" r="2" /><circle cx="12" cy="18" r="2" /><path d="M7 6h10M6 8l5 8M18 8l-5 8" /></>,
  events: <><path d="M4 5h16v14H4z" /><path d="M4 9h16M9 5v14" /></>,
  alerts: <><path d="M12 3a6 6 0 0 0-6 6c0 7-3 7-3 9h18c0-2-3-2-3-9a6 6 0 0 0-6-6z" /><path d="M10 21a2 2 0 0 0 4 0" /></>,
  sources: <><ellipse cx="12" cy="5" rx="8" ry="3" /><path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5" /><path d="M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" /></>,
  system: <><rect x="3" y="4" width="18" height="12" rx="1" /><path d="M8 20h8M12 16v4" /></>,
  account: <><circle cx="12" cy="8" r="4" /><path d="M5 20c0-3.3 3.1-5 7-5s7 1.7 7 5" /></>,
  team: <><circle cx="9" cy="8" r="3" /><circle cx="17" cy="9" r="2.5" /><path d="M3 19c0-3 2.7-4.5 6-4.5s6 1.5 6 4.5" /><path d="M15 14.5c2.6.2 4.5 1.6 4.5 4" /></>,
  teams: <><rect x="3" y="8" width="8" height="12" rx="1" /><rect x="13" y="4" width="8" height="16" rx="1" /><path d="M6 12h2M6 15h2M16 8h2M16 11h2M16 14h2" /></>,
};

function NavIcon({ name }: IconProps): ReactNode {
  return (
    <svg
      className="sb-item__icon"
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {PATHS[name] ?? PATHS["overview"]}
    </svg>
  );
}

// -------------------------------------------------------------------------------------------------
// Sidebar
// -------------------------------------------------------------------------------------------------

export function Sidebar(): ReactNode {
  return (
    <aside className="sidebar" aria-label="Primary">
      <div className="sidebar__brand">
        <img src={controlplaneLogo} alt="" className="sidebar__logo" />
        <span className="sidebar__wordmark">
          <span className="sidebar__wordmark-name">OATHERA</span>
          <span className="sidebar__wordmark-sub">CONTROL PLANE</span>
        </span>
      </div>

      <SidebarNav />

      {/* The signed-in operator: a single pill (avatar + name + team) that opens a popover with
          Account Management, Teams (superadmin) and Team Members (admin). Replaces the old
          always-expanded Account section. */}
      <div className="sidebar__bottom">
        <UserMenu />
      </div>
    </aside>
  );
}

/**
 * The primary navigation: Overview as a standalone link, then the functional groups rendered as a
 * SINGLE-OPEN accordion (opening one group closes the others). The group containing the current page
 * is open on load and whenever navigation moves into another group. Clicking a group header toggles
 * it; clicking a link navigates. On the narrow icon-rail the group headers are hidden by CSS and all
 * items show as icons, so navigation never becomes unreachable when collapsed.
 */
function SidebarNav(): ReactNode {
  const { pathname } = useLocation();

  // Which group owns the current path (longest-matching item path wins; "/" is Overview, no group).
  const activeGroupId = useMemo(() => groupForPath(pathname), [pathname]);

  // Single-open accordion: the open group id. It follows the active group as the route changes, but
  // the operator can still open a different group manually (until they navigate again).
  const [openId, setOpenId] = useState<string | null>(activeGroupId);
  const [pinnedPath, setPinnedPath] = useState(pathname);
  if (pathname !== pinnedPath) {
    // Navigation happened: re-sync the open group to wherever we landed.
    setPinnedPath(pathname);
    setOpenId(activeGroupId);
  }

  return (
    <nav className="sidebar__nav" aria-label="Sections">
      <NavLink
        to={NAV_OVERVIEW.path}
        end
        className={({ isActive }) => `sb-item${isActive ? " sb-item--active" : ""}`}
        title={NAV_OVERVIEW.label}
      >
        <NavIcon name={NAV_OVERVIEW.icon} />
        <span className="sb-item__label">{NAV_OVERVIEW.label}</span>
      </NavLink>

      {NAV_GROUPS.map((group) => (
        <AccordionGroup
          key={group.id}
          group={group}
          open={openId === group.id}
          hasActive={activeGroupId === group.id}
          onToggle={() => setOpenId((cur) => (cur === group.id ? null : group.id))}
        />
      ))}
    </nav>
  );
}

/** One collapsible group header + its links. */
function AccordionGroup(props: {
  group: NavGroup;
  open: boolean;
  hasActive: boolean;
  onToggle: () => void;
}): ReactNode {
  const { group, open, hasActive } = props;
  const regionId = `sb-group-${group.id}`;
  return (
    <div className="sb-group" data-open={open ? "true" : "false"}>
      <button
        type="button"
        className={`sb-group__header${hasActive ? " sb-group__header--active" : ""}`}
        aria-expanded={open}
        aria-controls={regionId}
        onClick={props.onToggle}
        title={group.label}
      >
        <NavIcon name={group.icon} />
        <span className="sb-item__label">{group.label}</span>
        <svg
          className="sb-group__chevron"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M9 6l6 6-6 6" />
        </svg>
      </button>
      <div id={regionId} className="sb-group__items" hidden={!open}>
        {group.items.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) => `sb-item sb-item--nested${isActive ? " sb-item--active" : ""}`}
            title={item.label}
          >
            <NavIcon name={item.icon} />
            <span className="sb-item__label">{item.label}</span>
          </NavLink>
        ))}
      </div>
    </div>
  );
}

/** The group id that owns `pathname`, or null (Overview / ungrouped). Longest item path wins. */
function groupForPath(pathname: string): string | null {
  if (pathname === "/") return null;
  let best: { id: string; len: number } | null = null;
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      if (item.path !== "/" && pathname.startsWith(item.path) && item.path.length > (best?.len ?? 0)) {
        best = { id: group.id, len: item.path.length };
      }
    }
  }
  return best?.id ?? null;
}

// -------------------------------------------------------------------------------------------------
// Top bar — breadcrumbs (left) + tenant/theme/connection controls (right)
// -------------------------------------------------------------------------------------------------

/** The breadcrumb label for the current path: the matching nav item, or the account/team screens. */
function useBreadcrumb(): string {
  const { pathname } = useLocation();
  if (pathname === "/account") return "Account";
  if (pathname === "/teams") return "Teams Management";
  if (pathname === "/members") return "Member Management";
  // Longest matching nav path wins (so "/traces/x" resolves to "Traces", "/" only matches exactly).
  const match = [...NAV_ITEMS]
    .filter((n) => (n.path === "/" ? pathname === "/" : pathname.startsWith(n.path)))
    .sort((a, b) => b.path.length - a.path.length)[0];
  return match?.label ?? "Overview";
}

/** The sun/moon control that flips the signed-in UI between light and dark. */
function ThemeToggle(): ReactNode {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";
  return (
    <button
      type="button"
      className="topbar__theme"
      onClick={toggleTheme}
      aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
      title={isDark ? "Switch to light theme" : "Switch to dark theme"}
    >
      {isDark ? (
        // Moon
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
        </svg>
      ) : (
        // Sun
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
        </svg>
      )}
    </button>
  );
}

export function Topbar(): ReactNode {
  const auth = useAuth();
  const org = auth.identity?.team?.name ?? auth.identity?.team?.id ?? "OATHERA";
  const here = useBreadcrumb();

  return (
    <header className="topbar">
      <nav className="topbar__crumbs" aria-label="Breadcrumb">
        <span className="topbar__org">{org}</span>
        <span className="topbar__sep" aria-hidden="true">/</span>
        <span className="topbar__here" aria-current="page">{here}</span>
      </nav>

      <button
        type="button"
        className="topbar__search"
        onClick={() => window.dispatchEvent(new CustomEvent("oa:open-palette"))}
        aria-label="Search and commands"
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4-4" />
        </svg>
        <span className="topbar__search-text">Search…</span>
        <kbd className="topbar__search-kbd">⌘K</kbd>
      </button>

      <div className="topbar__right">
        <TenantSwitcher />
        <ThemeToggle />
        <ConnectionIndicator />
      </div>
    </header>
  );
}
